import React, { memo } from 'react'
import { Grid, Stack } from '@mui/material'
import { faCoins } from '@fortawesome/pro-light-svg-icons'
import { faEthereum } from '@fortawesome/free-brands-svg-icons'
import { Trans } from 'react-i18next'

import BaseCard from '../shared/baseCard/BaseCard'
import Text from '../shared/text/Text'
import DefaultText from '../shared/text/DefaultText'
import QBXBanner from './QBXBanner'
import ParticipationDetails from './ParticipationDetails'
import DisplayStats from './DisplayStats'
import { formatEthCurrency } from '../../util/numbers.helpers'
import { CLAIM_PAGE_URL, CURRENCIES, ENABLE_CLAIM } from '../../constants/app'
import CustomButton from '../shared/buttons/CustomButton'
import { openBlankRedirectPage } from '../../util/app.helpers'
import CustomAlert from '../shared/CustomAlert/CustomAlert'
import RedeemPoints from './RedeemPoints'
import { MAIN_PROFILE_DISPATCH } from '../../store/reducers/main'
import { transWithArgs } from '../../util/validation.helpers'

const SaleResults = ({
  t,
  isWalletConnected,
  mainProfile,
  onClickRefund,
  claimStakeProps,
  dispatchAction
}) => {
  const {
    preSaleDepositPrice,
    publicSaleDepositPrice,
    preSaleTicketsWon,
    publicSaleTicketsWon,
    resultUserDeposit,
    userDeposit,
    ticketsWonETH,
    preSaleQBX,
    publicSaleQBX,
    preRaffleDeposited,
    publicRaffleDeposited,
    refundProcessing,
    error
  } = mainProfile
  const refundedAmount =
    userDeposit <= 0 ? resultUserDeposit - ticketsWonETH : 0
  const refundAmount =
    refundedAmount > 0 ? userDeposit : resultUserDeposit - ticketsWonETH
  const saleData = [
    {
      label: t('home.your-deposit'),
      value: formatEthCurrency(resultUserDeposit),
      hoverText: formatEthCurrency(resultUserDeposit, 6)
    },
    {
      label: t('results.tickets-won'),
      value: formatEthCurrency(ticketsWonETH),
      hoverText: formatEthCurrency(ticketsWonETH, 6)
    },
    {
      label: t('results.refund-amount'),
      value: formatEthCurrency(refundAmount),
      hoverText: formatEthCurrency(refundAmount, 6),
      dataDescStyles: {
        color: 'primary.main'
      }
    }
  ]
  const refundError = error[MAIN_PROFILE_DISPATCH.REFUND]
  return (
    <BaseCard>
      <Text variant='h2' label={t('results.title')} fontWeight={700} />
      <DefaultText label={t('results.description')} sx={{ mb: 4 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ParticipationDetails
                    t={t}
                    price={preSaleDepositPrice}
                    ticketsBought={preRaffleDeposited}
                    ticketsWon={preSaleTicketsWon}
                    qbxAmount={preSaleQBX}
                    title='pre-sale-participation'
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ParticipationDetails
                    t={t}
                    price={publicSaleDepositPrice}
                    ticketsBought={publicRaffleDeposited}
                    ticketsWon={publicSaleTicketsWon}
                    qbxAmount={publicSaleQBX}
                    title='public-sale-participation'
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <DisplayStats
                data={saleData}
                descStyles={{
                  color: '',
                  fontWeight: 900,
                  sx: {
                    fontSize: '2.625rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {(refundProcessing || refundError) && (
                <CustomAlert
                  error={refundError && transWithArgs(t, refundError)}
                  info={refundProcessing && t('home.transaction-processing')}
                  onTimeOut={
                    refundProcessing
                      ? () => {
                          dispatchAction(
                            MAIN_PROFILE_DISPATCH.REFUND_PROCESSING,
                            false
                          )
                        }
                      : null
                  }
                />
              )}
              {isWalletConnected && refundedAmount > 0 && (
                <DefaultText
                  label={
                    <Trans
                      i18nKey='home.refunded-info'
                      values={{
                        value: formatEthCurrency(refundedAmount)
                      }}
                    />
                  }
                />
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                <CustomButton
                  icon={faEthereum}
                  disabled={
                    !isWalletConnected || userDeposit <= 0 || refundProcessing
                  }
                  label={`${t('results.refund')} ${CURRENCIES.DEFAULT}`}
                  onClick={onClickRefund}
                />
                <CustomButton
                  icon={faCoins}
                  disabled={!isWalletConnected || !ENABLE_CLAIM}
                  label={t('results.go-to-claim-page')}
                  onClick={() => openBlankRedirectPage(CLAIM_PAGE_URL)}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <RedeemPoints
                t={t}
                mainProfile={mainProfile}
                {...claimStakeProps}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4} sx={{ pt: '0 !important' }}>
          <QBXBanner t={t}>
            <DefaultText
              label={t('results.thank-you-for-participating-refund-funds')}
              textAlign='center'
            />
          </QBXBanner>
        </Grid>
      </Grid>
    </BaseCard>
  )
}

export default memo(SaleResults)
